import { v4 as uuidv4 } from 'uuid'
import currencyFormatter from 'currency-formatter'
import prettyBytes from 'pretty-bytes'
import FileDownload from 'js-file-download'
import Location, { LocationDetail } from './types/Location'
import moment from 'moment-timezone'

export const offlineMode = process.env.NODE_ENV === 'production' ? false : true

export const backendDomain = process.env.NODE_ENV === 'production' ? '' : ''

export const frontendDomain = process.env.NODE_ENV === 'production' ? '' : ''

export const s3PublicStoragePrefix = ''

export const anonLocalStorageIdentifierKey = 'donata-anon-identifier'

export const imageMaxFileSizeInBytes = 1000000
export const imageMimeTypes = ['image/*']

export const fileMaxFileSizeInBytes = 100000000
export const fileMimeTypes = ['application/pdf']

export const maxInputLength = 255

export const defaultTaxRate = 19

export const getUUID = () => uuidv4()

export const getPercentage = (value: number, percentage: number) => {
  if (!value || !percentage) return 0
  return ((value || 0) / 100) * percentage
}

export const round = (value: number, toFixed = 2) => {
  return Number.parseFloat(value.toFixed(toFixed))
}

export const roundUp = (value: number) => {
  return Math.ceil(value)
}

export const roundDown = (value: number) => {
  return Math.floor(value)
}

export const getDateInputFormat = (date: Date) => {
  const fill = (number: number) => {
    const numberAsString = `${number}`
    return numberAsString.length === 1 ? `0${number}` : number
  }

  const day = fill(date.getDate())
  const month = fill(date.getMonth() + 1)
  const year = date.getFullYear()
  return `${year}-${month}-${day}`
}

export const getDateTimeInputFormat = (date: Date) => {
  const fill = (number: number) => {
    const numberAsString = `${number}`
    return numberAsString.length === 1 ? `0${number}` : number
  }

  const day = fill(date.getDate())
  const month = fill(date.getMonth() + 1)
  const year = date.getFullYear()
  const minute = fill(date.getMinutes())
  const hour = fill(date.getHours())
  return `${year}-${month}-${day}T${hour}:${minute}`
}

export const formatFileSize = (sizeInBytes, locale = 'de') => {
  return prettyBytes(sizeInBytes, { locale })
}

export const triggerClientSideDownload = (data: string, filename: string) => FileDownload(data, filename)

export const downloadFile = async (name: string, blobData: Blob) => {
  const text = await blobData.text()
  const newBlob = new Blob([text], {
    type: 'text/plain;charset=utf-8',
  })
  var link = document.createElement('a')
  link.href = window.URL.createObjectURL(newBlob)
  link.download = name
  link.click()
}

export const downloadFileFromURL = (name: string, url: string) => {
  var link = document.createElement('a')
  link.href = url
  link.download = name
  link.dispatchEvent(new MouseEvent('click'))
}

export const formatEUR = (amount) => {
  return currencyFormatter.format(amount, {
    locale: 'de-DE',
    symbol: '€',
    decimal: ',',
    thousand: '.',
    format: '%v %s',
  })
}

export const formatNumber = (value: number, precision = 2) => {
  return new Intl.NumberFormat('de-DE', {
    maximumFractionDigits: precision,
  }).format(value)
}

export const validateMail = (mail) => {
  const mailRegex = new RegExp('^\\S+@\\S+\\.\\S+$')
  return mailRegex.test(mail)
}

const fill = (number: Number) => {
  const numberAsString = `${number}`
  return numberAsString.length === 1 ? `0${numberAsString}` : numberAsString
}

export const prettyLocation = (location: Location): string => {
  if (!location) return ''
  return location.name
}

export const prettyLocationDetailCity = (location: LocationDetail): string => {
  if (!location) return ''
  const city = location.address_components.find((c) => c.types.includes('locality'))
  const postcode = location.address_components.find((c) => c.types.includes('postal_code'))
  const state = location.address_components.find((c) => c.types.includes('administrative_area_level_1'))
  if (city && postcode && state) {
    if (city.long_name === state.long_name) return `${postcode.long_name}, ${city.long_name}`
    return `${postcode.long_name} ${city.long_name}, ${state.long_name}`
  }
  if (city && postcode) return `${postcode.long_name} ${city.long_name}`
  if (city) return city.long_name
  if (postcode) return postcode.long_name
  return ''
}

export const getPrettyTime = (inputDate: Date) => {
  const date = moment.tz(inputDate, moment.tz.guess())
  const minute = fill(date.minutes())
  const hour = fill(date.hours())
  return `${hour}:${minute}`
}
export const getPrettyDateTime = (inputDate: Date) => {
  const date = moment.tz(inputDate, moment.tz.guess())
  const day = fill(date.date())
  const month = fill(date.month() + 1)
  const year = date.year()
  const minute = fill(date.minutes())
  const hour = fill(date.hours())
  return `${day}.${month}.${year} ${hour}:${minute}`
}
export const getPrettyDateTimeWithTimezone = (dateString: Date) => {
  const date = moment.utc(dateString).local()

  const day = fill(date.date())
  const month = fill(date.month() + 1)
  const year = date.year()
  const minute = fill(date.minutes())
  const hour = fill(date.hours())
  return `${day}.${month}.${year} ${hour}:${minute}`
}
export const getPrettyDate = (inputDate: Date) => {
  const date = moment.tz(inputDate, moment.tz.guess())
  const day = fill(date.date())
  const month = fill(date.month() + 1)
  const year = date.year()
  return `${day}.${month}.${year}`
}

export const getNameSuffix = (t: any, firstname: string) => {
  const useSpecialIndexOnNamesWithEnding = ['s', 'z', 'x']
  if (!firstname) return `${t.index.uploadForm.story.placeholderName}${t.generic.firstnameSuffix}`
  if (!!useSpecialIndexOnNamesWithEnding.includes(firstname.trim()[firstname.trim().length - 1]))
    return `${firstname.trim()}${t.generic.firstnameSpecialSuffix}`
  return `${firstname.trim()}${t.generic.firstnameSuffix}`
}

export const calculateDistanceInKM = (location1: Location, location2: Location) => {
  const R = 6371 // Earth's radius in kilometers

  const toRadians = (degree: number): number => (degree * Math.PI) / 180

  const dLat = toRadians(location2.coordinates[0] - location1.coordinates[0])
  const dLon = toRadians(location2.coordinates[1] - location1.coordinates[1])

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(location1.coordinates[0])) * Math.cos(toRadians(location2.coordinates[0])) * Math.sin(dLon / 2) * Math.sin(dLon / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

export const prettyDistance = (distanceInKm: number) => {
  if (distanceInKm < 1) return `${formatNumber(distanceInKm * 1000)} m`
  return `${formatNumber(distanceInKm)} km`
}
